import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';

const WishlistDetail = () => {
  const { id, token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentFamily } = useFamily();
  const [wishlist, setWishlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemFormData, setItemFormData] = useState({
    name: '',
    description: '',
    link: '',
    image_url: '',
    priority: 0,
  });
  const [extractingImage, setExtractingImage] = useState(false);
  const [fetchImageFailed, setFetchImageFailed] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [linkType, setLinkType] = useState('url'); // 'url' or 'text'

  const fetchWishlist = async () => {
    try {
      let response;
      if (token) {
        response = await api.get(`/wishlists/shared/${token}/`);
      } else {
        response = await api.get(`/wishlists/${id}/`);
      }
      setWishlist(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWishlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  const handleAddItem = async () => {
    if (!currentFamily && !token) {
      alert('Please select a family first');
      return;
    }
    try {
      let response;
      if (imageFile) {
        // Use FormData if there's an image file
        const formDataToSend = new FormData();
        formDataToSend.append('name', itemFormData.name);
        formDataToSend.append('description', itemFormData.description || '');
        formDataToSend.append('link', itemFormData.link || '');
        if (itemFormData.image_url) {
          formDataToSend.append('image_url', itemFormData.image_url);
        }
        formDataToSend.append('priority', itemFormData.priority.toString());
        formDataToSend.append('image', imageFile);
        
        response = await api.post(`/wishlists/${wishlist.id}/add_item/`, formDataToSend);
      } else {
        response = await api.post(`/wishlists/${wishlist.id}/add_item/`, itemFormData);
      }
      
      // Update wishlist with new item
      setWishlist({
        ...wishlist,
        items: [...(wishlist.items || []), response.data],
      });
      setItemDialogOpen(false);
      setEditingItemId(null);
      setItemFormData({
        name: '',
        description: '',
        link: '',
        image_url: '',
        priority: 0,
      });
      setImageFile(null);
      setImagePreview(null);
    } catch (error) {
      alert('Failed to add item');
    }
  };

  const handleEditItem = (item) => {
    setEditingItemId(item.id);
    // Determine link type based on whether it looks like a URL
    const isUrl = item.link && (item.link.startsWith('http://') || item.link.startsWith('https://') || item.link.startsWith('www.'));
    setLinkType(isUrl ? 'url' : 'text');
    setItemFormData({
      name: item.name,
      description: item.description || '',
      link: item.link || '',
      image_url: item.image_url || '',
      priority: item.priority || 0,
    });
    setImageFile(null);
    setImagePreview(null);
    setItemDialogOpen(true);
  };

  const handleUpdateItem = async () => {
    try {
      let response;
      if (imageFile) {
        // Use FormData if there's an image file
        const formDataToSend = new FormData();
        formDataToSend.append('name', itemFormData.name);
        formDataToSend.append('description', itemFormData.description || '');
        formDataToSend.append('link', itemFormData.link || '');
        if (itemFormData.image_url) {
          formDataToSend.append('image_url', itemFormData.image_url);
        }
        formDataToSend.append('priority', itemFormData.priority.toString());
        formDataToSend.append('image', imageFile);
        
        response = await api.put(
          `/wishlists/${wishlist.id}/items/${editingItemId}/update/`,
          formDataToSend
        );
      } else {
        response = await api.put(
          `/wishlists/${wishlist.id}/items/${editingItemId}/update/`,
          itemFormData
        );
      }
      
      setWishlist({
        ...wishlist,
        items: wishlist.items.map((item) =>
          item.id === editingItemId ? response.data : item
        ),
      });
      setItemDialogOpen(false);
      setEditingItemId(null);
      setItemFormData({
        name: '',
        description: '',
        link: '',
        image_url: '',
        priority: 0,
      });
      setImageFile(null);
      setImagePreview(null);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          error.message || 
                          'Failed to update item';
      alert(errorMessage);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/wishlists/${wishlist.id}/items/${itemToDelete.id}/delete/`);
      setWishlist({
        ...wishlist,
        items: wishlist.items.filter((item) => item.id !== itemToDelete.id),
      });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      alert('Failed to delete item');
    }
  };

  const handleMarkPurchased = async (itemId) => {
    try {
      const response = await api.post(
        `/wishlists/${wishlist.id}/items/${itemId}/purchase/`
      );
      setWishlist({
        ...wishlist,
        items: wishlist.items.map((item) =>
          item.id === itemId ? response.data : item
        ),
      });
    } catch (error) {
      alert('Failed to mark as purchased');
    }
  };

  const handleUnmarkPurchased = async (itemId) => {
    try {
      const response = await api.put(
        `/wishlists/${wishlist.id}/items/${itemId}/update/`,
        { is_purchased: false }
      );
      setWishlist({
        ...wishlist,
        items: wishlist.items.map((item) =>
          item.id === itemId ? response.data : item
        ),
      });
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to unmark as purchased');
    }
  };

  const handleTogglePurchased = async (item) => {
    try {
      const response = await api.put(
        `/wishlists/${wishlist.id}/items/${item.id}/update/`,
        { is_purchased: !item.is_purchased }
      );
      setWishlist({
        ...wishlist,
        items: wishlist.items.map((wishlistItem) =>
          wishlistItem.id === item.id ? response.data : wishlistItem
        ),
      });
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to toggle purchase status');
    }
  };

  const handleLinkChange = (e) => {
    const link = e.target.value;
    setItemFormData({ ...itemFormData, link });
    
    // Only try to extract name from URL if it's a valid URL
    if (link && !itemFormData.name && linkType === 'url') {
      try {
        const urlObj = new URL(link);
        const defaultName = urlObj.pathname.split('/').filter(p => p).pop() || urlObj.hostname.replace('www.', '');
        setItemFormData(prev => ({
          ...prev,
          link,
          name: defaultName.length > 50 ? defaultName.substring(0, 50) : defaultName
        }));
      } catch {
        // Invalid URL, keep as is
      }
    }
  };

  // Auto-extract image when link changes (debounced) - only for URLs
  useEffect(() => {
    const link = itemFormData.link;
    
    // Only auto-extract if:
    // 1. Link is provided
    // 2. Link type is 'url' (not 'text')
    // 3. Image URL is empty (user hasn't manually set one)
    // 4. We're not currently editing (to avoid overwriting during edit)
    if (link && linkType === 'url' && !itemFormData.image_url && !editingItemId) {
      // Validate URL format
      try {
        new URL(link);
        
        // Debounce: wait 1.5 seconds after user stops typing
        setExtractingImage(true);
        const timeoutId = setTimeout(async () => {
          try {
            const response = await api.post('/wishlists/extract-images/', { url: link });
            const images = response.data.images || [];
            
            if (images.length > 0) {
              // Use the first (best) image
              const bestImage = images[0].url;
              setItemFormData(prev => ({
                ...prev,
                image_url: bestImage
              }));
            }
          } catch (error) {
            // Silently fail - user can still add image manually
          } finally {
            setExtractingImage(false);
          }
        }, 1500); // Wait 1.5 seconds after user stops typing

        return () => {
          clearTimeout(timeoutId);
          setExtractingImage(false);
        };
      } catch {
        // Invalid URL, don't try to extract
        setExtractingImage(false);
      }
    } else {
      setExtractingImage(false);
    }
  }, [itemFormData.link, itemFormData.image_url, editingItemId, linkType]);


  const copyShareLink = () => {
    const url = token
      ? window.location.href
      : `${window.location.origin}/wishlists/shared/${wishlist.share_token}`;
    navigator.clipboard.writeText(url);
    alert('Share link copied to clipboard!');
  };

  const isOwner = user && wishlist && user.uid === wishlist.user_id;
  
  // Check if user is a family admin
  const [familyData, setFamilyData] = useState(null);
  useEffect(() => {
    const fetchFamilyData = async () => {
      if (currentFamily && currentFamily.id) {
        try {
          const response = await api.get(`/families/${currentFamily.id}/`);
          setFamilyData(response.data);
        } catch (error) {
        }
      }
    };
    if (currentFamily && !token) {
      fetchFamilyData();
    }
  }, [currentFamily, token]);
  
  const userMember = familyData?.members?.find((m) => m.user_id === user?.uid);
  const isAdmin = userMember && ['owner', 'admin'].includes(userMember.role);
  const canEdit = isOwner || isAdmin;
  // Owner cannot see purchase status regardless of admin role
  const canSeePurchaseStatus = !isOwner;

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!wishlist) {
    return (
      <Container>
        <Typography>Wishlist not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {!token && (
        <Button onClick={() => navigate('/wishlists')} sx={{ mb: 2 }}>
          ← Back to Wishlists
        </Button>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start">
            <Box>
              <Typography variant="h4" gutterBottom>
                {wishlist.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {wishlist.user_name}'s Wishlist
              </Typography>
            </Box>
            {canEdit && (
              <Box display="flex" gap={1}>
                <Tooltip title="Copy share link">
                  <IconButton onClick={copyShareLink}>
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete wishlist">
                  <IconButton 
                    color="error"
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to delete this wishlist? This action cannot be undone.')) {
                        try {
                          await api.delete(`/wishlists/${wishlist.id}/`);
                          navigate('/wishlists');
                        } catch (error) {
                          alert(error.response?.data?.error || 'Failed to delete wishlist');
                        }
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {canEdit && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Items</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingItemId(null);
              setItemFormData({
                name: '',
                description: '',
                link: '',
                image_url: '',
                priority: 0,
              });
              setLinkType('url'); // Reset to URL by default
              setItemDialogOpen(true);
            }}
          >
            Add Item
          </Button>
        </Box>
      )}

      {wishlist.items?.length === 0 ? (
        <Card>
          <CardContent>
            <Typography>No items yet. Add some items to your wishlist!</Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {wishlist.items?.map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s, opacity 0.2s',
                  position: 'relative',
                  // Show purchase styling if can see purchase status
                  opacity: (canSeePurchaseStatus && item.is_purchased) ? 0.7 : 1,
                  border: (canSeePurchaseStatus && item.is_purchased) ? '2px solid' : 'none',
                  borderColor: (canSeePurchaseStatus && item.is_purchased) ? 'success.main' : 'transparent',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                    // Enhance hover effect on purchased items
                    opacity: (canSeePurchaseStatus && item.is_purchased) ? 0.9 : 1,
                  },
                }}
              >
                {/* Show purchase badge if can see purchase status (admins can see even on own wishlist) */}
                {canSeePurchaseStatus && item.is_purchased && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 10,
                      bgcolor: 'success.main',
                      color: 'white',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      boxShadow: 3,
                      fontWeight: 'bold',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                      ✓ PURCHASED
                    </Typography>
                  </Box>
                )}
                {(item.image_url_display || item.image_url) ? (
                  <CardMedia
                    component="img"
                    height={{ xs: 200, sm: 250 }}
                    image={item.image_url_display || item.image_url}
                    alt={item.name}
                    sx={{ 
                      objectFit: 'cover',
                      // Apply grayscale if can see purchase status
                      filter: (canSeePurchaseStatus && item.is_purchased) ? 'grayscale(50%)' : 'none',
                    }}
                    onError={(e) => {
                      // Hide the broken image and show placeholder
                      e.target.style.display = 'none';
                      const placeholder = e.target.parentElement.querySelector('.image-placeholder');
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                  />
                ) : null}
                {(!item.image_url_display && !item.image_url) && (
                  <Box
                    className="image-placeholder"
                    sx={{
                      height: { xs: 200, sm: 250 },
                      bgcolor: 'grey.200',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No Image
                    </Typography>
                  </Box>
                )}
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: { xs: 1.5, sm: 2 } }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    noWrap 
                    sx={{ 
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      // Show line-through if can see purchase status
                      textDecoration: (canSeePurchaseStatus && item.is_purchased) ? 'line-through' : 'none',
                      color: (canSeePurchaseStatus && item.is_purchased) ? 'text.secondary' : 'text.primary',
                    }}
                  >
                    {item.name}
                  </Typography>
                  {item.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        fontSize: { xs: '0.85rem', sm: '0.875rem' },
                      }}
                    >
                      {item.description}
                    </Typography>
                  )}
                  <Box sx={{ mt: 'auto', pt: 1 }}>
                    <Box sx={{ mb: 1 }}>
                      {/* Show purchase chip if can see purchase status (admins can see even on own wishlist) */}
                      {canSeePurchaseStatus && item.is_purchased ? (
                        <Chip 
                          label="✓ Purchased" 
                          color="success" 
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      ) : (
                        <Chip
                          label={`Priority: ${item.priority}`}
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    {item.link && (
                      (item.link.startsWith('http://') || item.link.startsWith('https://') || item.link.startsWith('www.')) ? (
                        <Button
                          fullWidth
                          size="small"
                          variant="contained"
                          startIcon={<OpenInNewIcon />}
                          href={item.link.startsWith('www.') ? `https://${item.link}` : item.link}
                          target="_blank"
                          sx={{ mb: 1 }}
                        >
                          View Link
                        </Button>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1,
                            p: 1,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          }}
                        >
                          📝 {item.link}
                        </Typography>
                      )
                    )}
                    {canEdit && (
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditItem(item)}
                        >
                          Edit
                        </Button>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteClick(item)}
                        >
                          Delete
                        </Button>
                      </Box>
                    )}
                    {/* Purchase status buttons - show for non-owners and admins (even on own wishlist) */}
                    {canSeePurchaseStatus && (
                      <>
                        {/* If item is purchased and user marked it, show unmark button */}
                        {item.is_purchased && item.purchased_by === user?.uid && (
                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleUnmarkPurchased(item.id)}
                          >
                            Unmark as Purchased
                          </Button>
                        )}
                        {/* If item is not purchased, show mark button */}
                        {!item.is_purchased && (
                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            onClick={() => handleMarkPurchased(item.id)}
                          >
                            Mark as Purchased
                          </Button>
                        )}
                        {/* Admin can toggle purchase status on any item (including their own wishlist) */}
                        {isAdmin && item.is_purchased && (
                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            color={item.purchased_by === user?.uid ? "warning" : "secondary"}
                            onClick={() => handleTogglePurchased(item)}
                            sx={{ mt: item.purchased_by === user?.uid ? 1 : 0 }}
                          >
                            Unmark as Purchased
                          </Button>
                        )}
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {canEdit && (
        <Dialog
          open={itemDialogOpen}
          onClose={() => {
            setItemDialogOpen(false);
            setEditingItemId(null);
            setItemFormData({
              name: '',
              description: '',
              link: '',
              image_url: '',
              priority: 0,
            });
            setImageFile(null);
            setImagePreview(null);
            setLinkType('url'); // Reset to URL by default
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{editingItemId ? 'Edit Wishlist Item' : 'Add Wishlist Item'}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Item Name"
              margin="normal"
              value={itemFormData.name}
              onChange={(e) =>
                setItemFormData({ ...itemFormData, name: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Description"
              margin="normal"
              multiline
              rows={2}
              value={itemFormData.description}
              onChange={(e) =>
                setItemFormData({ ...itemFormData, description: e.target.value })
              }
            />
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="body2" gutterBottom>
                Link Type:
              </Typography>
              <ToggleButtonGroup
                value={linkType}
                exclusive
                onChange={(e, newType) => {
                  if (newType !== null) {
                    setLinkType(newType);
                    // Clear link when switching types
                    setItemFormData({ ...itemFormData, link: '' });
                  }
                }}
                size="small"
                fullWidth
              >
                <ToggleButton value="url">URL</ToggleButton>
                <ToggleButton value="text">Text</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <TextField
              fullWidth
              label={linkType === 'url' ? 'Link (URL)' : 'Link (Text)'}
              margin="normal"
              value={itemFormData.link}
              onChange={linkType === 'url' ? handleLinkChange : (e) => {
                setItemFormData({ ...itemFormData, link: e.target.value });
              }}
              helperText={
                linkType === 'url' 
                  ? (extractingImage 
                      ? "Extracting image from link..." 
                      : "Image will be automatically extracted from the link. If it doesn't work, you can add an image URL manually below.")
                  : "Enter any text or note about where to find this item (e.g., 'Target, aisle 5' or 'Amazon - search for blue sweater')"
              }
              placeholder={linkType === 'url' ? "https://example.com/product" : "e.g., Target, aisle 5 or Amazon - search for blue sweater"}
              InputProps={{
                endAdornment: linkType === 'url' && extractingImage ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : null
              }}
            />
            <TextField
              fullWidth
              type="file"
              accept="image/*"
              label="Upload Image"
              margin="normal"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setImageFile(file);
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setImagePreview(reader.result);
                  };
                  reader.readAsDataURL(file);
                }
              }}
              helperText="Upload an image for the wishlist item"
              InputLabelProps={{ shrink: true }}
            />
            {imagePreview && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }}
                />
              </Box>
            )}
            <TextField
              fullWidth
              label="Image URL (optional - alternative to upload)"
              margin="normal"
              value={itemFormData.image_url}
              onChange={(e) =>
                setItemFormData({ ...itemFormData, image_url: e.target.value })
              }
              helperText="Or paste an image URL instead of uploading"
              placeholder="https://example.com/image.jpg"
            />
            {editingItemId && itemFormData.link && linkType === 'url' && !itemFormData.image_url && (
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={async () => {
                    setExtractingImage(true);
                    setFetchImageFailed(false);
                    try {
                      const response = await api.post('/wishlists/extract-images/', { url: itemFormData.link });
                      const images = response.data.images || [];
                      if (images.length > 0) {
                        setItemFormData(prev => ({ ...prev, image_url: images[0].url }));
                        setFetchImageFailed(false);
                      } else {
                        setFetchImageFailed(true);
                      }
                    } catch {
                      setFetchImageFailed(true);
                    } finally {
                      setExtractingImage(false);
                    }
                  }}
                  disabled={extractingImage}
                >
                  {extractingImage ? <><CircularProgress size={16} sx={{ mr: 1 }} />Fetching...</> : 'Fetch Image from Link'}
                </Button>
                {fetchImageFailed && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Couldn't find an image automatically. Try pasting an image URL in the field above.
                  </Typography>
                )}
              </Box>
            )}

            {/* Preview Card */}
            {itemFormData.link && itemFormData.name && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Preview:
                </Typography>
                <Card
                  sx={{
                    border: '1px dashed',
                    borderColor: 'primary.main',
                    bgcolor: 'grey.50',
                  }}
                >
                  {(imagePreview || itemFormData.image_url) ? (
                    <CardMedia
                      component="img"
                      height={200}
                      image={imagePreview || itemFormData.image_url}
                      alt={itemFormData.name}
                      sx={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 200,
                        bgcolor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Image will appear here
                      </Typography>
                    </Box>
                  )}
                  <CardContent>
                    <Typography variant="h6" gutterBottom noWrap>
                      {itemFormData.name || 'Item Name'}
                    </Typography>
                    {itemFormData.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {itemFormData.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip
                        label={`Priority: ${itemFormData.priority}`}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                      {itemFormData.link && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<OpenInNewIcon />}
                          href={itemFormData.link}
                          target="_blank"
                        >
                          View Link
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}
            <TextField
              fullWidth
              label="Priority (higher = more wanted)"
              type="number"
              margin="normal"
              value={itemFormData.priority}
              onChange={(e) =>
                setItemFormData({
                  ...itemFormData,
                  priority: parseInt(e.target.value) || 0,
                })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setItemDialogOpen(false);
                setEditingItemId(null);
                setItemFormData({
                  name: '',
                  description: '',
                  link: '',
                  image_url: '',
                  priority: 0,
                });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={editingItemId ? handleUpdateItem : handleAddItem}
              variant="contained"
            >
              {editingItemId ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
      >
        <DialogTitle>Delete Item</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{itemToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setItemToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default WishlistDetail;
